import { useRef, type ChangeEvent, type ReactNode } from "react";
import styled from "styled-components";

import { CloseIcon, SearchIcon as SearchGlyph } from "../../icons/glyphs";
import { controlFocusRing, controlRadius, controlTransition } from "../foundations/controlStyles";

export type DropdownSearchFieldProps = {
  value: string;
  onValueChange: (value: string) => void;
  placeholder: string;
  clearLabel?: string;
  action?: ReactNode;
};

const SearchRow = styled.div<{ $hasAction: boolean }>`
  display: grid;
  grid-template-columns: ${({ $hasAction }) =>
    $hasAction ? "minmax(0, 1fr) auto" : "minmax(0, 1fr)"};
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.color.border.subtle};
  background-color: ${({ theme }) => theme.color.bg.surfaceRaised};
`;

const SearchActionSlot = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
`;

const SearchField = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.55rem;
  ${controlRadius}
  /* 입력류 rest 보더는 subtle — 형제 컨트롤(ContentSwitcher 트랙)과 같은 단으로 톤다운. */
  border: 1px solid ${({ theme }) => theme.color.border.subtle};
  background-color: ${({ theme }) => theme.color.bg.surface};
  color: ${({ theme }) => theme.color.text.default};
  ${controlTransition("border-color", "background-color")}

  /* 자체 컨테이너 border를 가진 입력류라 focus는 outline ring 없이 border-color만 바꾼다
     (Input family의 InputControl과 동일) — ring을 더하면 테두리가 두 겹으로 보인다. */
  &:focus-within {
    border-color: ${({ theme }) => theme.color.border.focus};
  }
`;

const SearchIcon = styled(SearchGlyph)`
  flex: 0 0 auto;
  color: ${({ theme }) => theme.color.text.muted};
`;

const SearchInput = styled.input`
  width: 100%;
  min-width: 0;
  padding: 0;
  border: 0;
  background-color: transparent;
  color: ${({ theme }) => theme.color.text.default};
  font: inherit;
  font-size: ${({ theme }) => theme.typography.size.body};
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme.color.text.muted};
  }

  &::-webkit-search-cancel-button {
    appearance: none;
    display: none;
  }
`;

const ClearSearchButton = styled.button`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  padding: 0;
  border: 0;
  background-color: transparent;
  color: ${({ theme }) => theme.color.text.muted};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.color.text.default};
  }

  &:focus-visible {
    ${controlFocusRing()}
  }
`;

/** Shared internal search header for the Single/MultiSelectDropdown popup families. */
export function DropdownSearchField({
  value,
  onValueChange,
  placeholder,
  clearLabel = "Clear search",
  action,
}: DropdownSearchFieldProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onValueChange(event.target.value);
  };

  const handleClear = () => {
    onValueChange("");
    inputRef.current?.focus({ preventScroll: true });
  };

  return (
    <SearchRow $hasAction={action != null}>
      <SearchField>
        <SearchIcon aria-hidden />
        <SearchInput
          ref={inputRef}
          type="search"
          value={value}
          placeholder={placeholder}
          aria-label={placeholder}
          onChange={handleChange}
        />
        {value ? (
          <ClearSearchButton type="button" aria-label={clearLabel} onClick={handleClear}>
            <CloseIcon aria-hidden />
          </ClearSearchButton>
        ) : null}
      </SearchField>
      {action != null ? <SearchActionSlot>{action}</SearchActionSlot> : null}
    </SearchRow>
  );
}
